import { useEffect } from 'react';
import { ROLE_ADMIN, ROLE_FARMER, ROLE_SUPER_ADMIN, ROLE_TECHNICIAN } from 'utils/const';

const useFilteredCulturesLocalStorage = (
  user,
  from,
  filteredCultures,
  userMaskedCulturesIndex,
  lastSelectedRows,
  setLastSelectedRows,
  toggleVisibilityFilter,
  setToggleVisibilityFilter,
  selectedRows,
  structureId,
  sectorId,
  exploitationId,
  organicState,
  hasToggleCultureShow,
  setHasToggleCultureShow,
) => {
  const mapIdsForJSON = (lastSelectedRows) => {
    let maskedRows = structuredClone(lastSelectedRows);
    if (maskedRows?.ids instanceof Set) {
      maskedRows.ids = Array.from(maskedRows.ids);
    }
    return maskedRows;
  };
  const updateMaskedCulturesObject = {
    ...(from === 'fromCooperative' && {
      structure: structureId,
    }),
    ...(from === 'fromSector' && { sector: sectorId }),
    ...(from === 'fromExploitation' && { exploitation: exploitationId }),
    maskedRows: structuredClone(lastSelectedRows),
  };

  const setFilteredCulturesLocalStorage = (array, index = null) => {
    // Ids is an set and we need to convert it to an array to be saved in localStorage
    // Because set is not json serializable and is replaced by an empty object
    if (index >= 0 && array?.maskedCulturesByLevel?.[index]) {
      mapIdsForJSON(array.maskedCulturesByLevel[index].maskedRows[organicState]);
    }
    localStorage.setItem('filteredCultures', JSON.stringify(array));
  };

  useEffect(() => {
    if (from === 'fromExploitation') return;
    setLastSelectedRows(
      filteredCultures && userMaskedCulturesIndex !== -1
        ? filteredCultures?.maskedCulturesByLevel[userMaskedCulturesIndex].maskedRows || []
        : [],
    );

    if (filteredCultures && userMaskedCulturesIndex !== -1) {
      setToggleVisibilityFilter({
        ...toggleVisibilityFilter,
        [organicState]: filteredCultures?.maskedCulturesByLevel?.[userMaskedCulturesIndex]
          ?.maskedRows?.[organicState]?.ids?.size
          ? true
          : false,
      });
    }
  }, [organicState]);

  useEffect(() => {
    if (from === 'fromExploitation') return;
    if (filteredCultures && userMaskedCulturesIndex !== -1 && hasToggleCultureShow) {
      if (lastSelectedRows?.[organicState]?.ids?.size) {
        if (
          !selectedRows?.[organicState]?.ids.size &&
          filteredCultures?.maskedCulturesByLevel?.[userMaskedCulturesIndex]?.maskedRows?.[
            organicState
          ]?.ids.size
        ) {
          filteredCultures.maskedCulturesByLevel[userMaskedCulturesIndex].maskedRows[organicState] =
            [];
          setFilteredCulturesLocalStorage(filteredCultures, userMaskedCulturesIndex);
          setHasToggleCultureShow(false);
        } else if (
          filteredCultures?.maskedCulturesByLevel?.[userMaskedCulturesIndex]?.maskedRows.length ===
          0
        ) {
          setLastSelectedRows([]);
          localStorage.removeItem('filteredCultures');
        }
      }
    }
  }, [selectedRows, filteredCultures, organicState]);

  useEffect(() => {
    if (from === 'fromExploitation') return;
    if (!filteredCultures || userMaskedCulturesIndex === -1) {
      return;
    }

    const maskedRows =
      filteredCultures?.maskedCulturesByLevel?.[userMaskedCulturesIndex]?.maskedRows || {};

    const updateMaskedRows = (newRows) => {
      if (filteredCultures?.maskedCulturesByLevel?.[userMaskedCulturesIndex]?.maskedRows) {
        filteredCultures.maskedCulturesByLevel[userMaskedCulturesIndex].maskedRows[organicState] =
          structuredClone(mapIdsForJSON(newRows));
        setFilteredCulturesLocalStorage(filteredCultures, userMaskedCulturesIndex);
      }
    };

    if (selectedRows?.[organicState]?.ids?.size) {
      if (toggleVisibilityFilter?.[organicState]) {
        updateMaskedRows(selectedRows?.[organicState]);
      } else {
        if (
          selectedRows?.[organicState]?.ids?.size < (maskedRows[organicState]?.ids?.length || 0)
        ) {
          setLastSelectedRows((prev) => ({
            ...prev,
            [organicState]: mapIdsForJSON({ ...selectedRows[organicState] }),
          }));
          updateMaskedRows(selectedRows[organicState]);
        }
      }
    } else if (!toggleVisibilityFilter?.[organicState]) {
      const isConventionalEmpty = !maskedRows.conventional?.ids?.size;
      const isOrganicEmpty = !maskedRows.organic?.ids?.size;
      if (isConventionalEmpty && isOrganicEmpty) {
        if (filteredCultures?.maskedCulturesByLevel?.length === 1) {
          setLastSelectedRows([]);
          localStorage.removeItem('filteredCultures');
        } else {
          filteredCultures.maskedCulturesByLevel.splice(userMaskedCulturesIndex, 1);
          setFilteredCulturesLocalStorage(filteredCultures, userMaskedCulturesIndex);
          setLastSelectedRows((prev) => ({
            ...prev,
            [organicState]: mapIdsForJSON({ ...selectedRows?.[organicState] }),
          }));
        }
      } else if (
        filteredCultures?.maskedRotationsByLevel?.[userMaskedCulturesIndex]?.maskedRows?.[
          organicState
        ]
      ) {
        delete filteredCultures.maskedRotationsByLevel[userMaskedCulturesIndex].maskedRows[
          organicState
        ];
        setFilteredCulturesLocalStorage(filteredCultures, userMaskedCulturesIndex);
      }
    }
  }, [selectedRows, filteredCultures, toggleVisibilityFilter]);

  if (from !== 'fromExploitation') {
    if (filteredCultures) {
      if (
        userMaskedCulturesIndex !== -1 &&
        lastSelectedRows?.[organicState]?.ids?.length >
          filteredCultures?.maskedCulturesByLevel?.[userMaskedCulturesIndex]?.maskedRows?.[
            organicState
          ]?.ids?.size
      ) {
        if (filteredCultures?.maskedCulturesByLevel?.[userMaskedCulturesIndex]?.maskedRows) {
          filteredCultures.maskedCulturesByLevel[userMaskedCulturesIndex].maskedRows[organicState] =
            mapIdsForJSON({
              ...lastSelectedRows[organicState],
            });
          setFilteredCulturesLocalStorage(filteredCultures, userMaskedCulturesIndex);
        }
      } else if (userMaskedCulturesIndex === -1 && lastSelectedRows?.[organicState]?.ids?.size) {
        updateMaskedCulturesObject.maskedRows[organicState] = mapIdsForJSON(
          structuredClone(updateMaskedCulturesObject.maskedRows[organicState]),
        );
        filteredCultures.maskedCulturesByLevel.push(structuredClone(updateMaskedCulturesObject));
        setFilteredCulturesLocalStorage(filteredCultures, userMaskedCulturesIndex);
      }
    } else if (
      userMaskedCulturesIndex === -1 &&
      lastSelectedRows?.[organicState]?.ids?.size &&
      selectedRows?.[organicState]?.ids?.size
    ) {
      let maskedCulturesByUser = {};
      switch (user?.role) {
        case 'SUPER_ADMIN':
          maskedCulturesByUser.role = ROLE_SUPER_ADMIN;
          break;
        case 'ADMIN':
          maskedCulturesByUser.role = ROLE_ADMIN;
          break;
        case 'TECHNICIAN':
          maskedCulturesByUser.role = ROLE_TECHNICIAN;
          break;
        case 'FARMER':
          maskedCulturesByUser.role = ROLE_FARMER;
          break;
      }

      updateMaskedCulturesObject.maskedRows[organicState] = mapIdsForJSON({
        ...updateMaskedCulturesObject.maskedRows[organicState],
      });

      maskedCulturesByUser.maskedCulturesByLevel = [{ ...updateMaskedCulturesObject }];
      setFilteredCulturesLocalStorage(maskedCulturesByUser);
    }
  }
};

export default useFilteredCulturesLocalStorage;
